import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/site";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, X, Save, Upload, ImageIcon, ImageOff, UtensilsCrossed, LayoutDashboard, Calendar, BarChart3, Images, Music, Users, Activity, Sparkles } from "lucide-react";
import { toast, Toaster } from "sonner";
import SiteEditor from "@/pages/admin/SiteEditor";
import LunchEditor from "@/pages/admin/LunchEditor";
import Analytics from "@/pages/admin/Analytics";
import GalleryEditor from "@/pages/admin/GalleryEditor";
import EventsEditor from "@/pages/admin/EventsEditor";
import UsersEditor from "@/pages/admin/UsersEditor";
import AuditLog from "@/pages/admin/AuditLog";
import DailySpecialEditor from "@/pages/admin/DailySpecialEditor";

const CATEGORIES = [
  "Ordinarie pizzor",
  "Salamipizzor",
  "Köttfärspizzor",
  "Havets pizzor",
  "Baconpizzor",
  "Vegetariska pizzor",
  "Mexicana pizzor",
  "Kycklingpizzor",
  "Amerikanska pizzor",
  "Inbakade pizzor",
  "Fläskfilépizzor (ubåt)",
  "Oxfilépizzor",
  "Biffpizzor",
  "Italienska pizzor - Vegetariana",
  "Italienska pizzor - Carne",
  "Nyhetspizza",
  "Rullar",
  "Kebab & kyckling",
  "Sallader",
  "Pasta",
  "Hamburgare",
  "Mexikanska rätter",
  "À la Carte",
  "Fiskrätter",
  "Barnmeny",
  "Familjepizzor",
  "Dryck & Tillbehör",
];

function formatError(detail) {
  if (!detail) return "Något gick fel";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function ItemModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(
    initial || { category: "Ordinarie pizzor", name: "", description: "", price: 0, sort_order: 0, image_url: "" }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const isNef = (file.name || "").toLowerCase().endsWith(".nef");
    if (!isNef && !file.type.startsWith("image/")) {
      toast.error("Filen måste vara en bild");
      return;
    }
    const limit = isNef ? 60 * 1024 * 1024 : 15 * 1024 * 1024;
    if (file.size > limit) {
      toast.error(`Bilden är för stor (max ${isNef ? 60 : 15} MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success(isNef ? "NEF konverterad och uppladdad" : "Bild uppladdad");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Kunde inte ladda upp bilden");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        price: Number(form.price),
        sort_order: Number(form.sort_order),
        image_url: form.image_url || null,
      });
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const preview = resolveImageUrl(form.image_url);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        data-testid="item-modal"
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden my-8"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <h3 className="font-serif-display text-2xl font-bold">
            {initial ? "Redigera maträtt" : "Ny maträtt"}
          </h3>
          <button type="button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--surface-alt)]">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Image */}
          <div>
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Bild</span>
            <div className="mt-2 flex items-start gap-4">
              <div className="w-28 h-28 shrink-0 rounded-xl bg-[var(--surface-alt)] border border-[var(--border)] overflow-hidden grid place-items-center">
                {preview ? (
                  <img src={preview} alt="förhandsvisning" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={28} className="text-[var(--text-muted)]" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.nef,image/x-nikon-nef"
                  ref={fileInputRef}
                  className="hidden"
                  data-testid="modal-image-file"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <button
                  type="button"
                  data-testid="modal-image-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--text)] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 disabled:opacity-60"
                >
                  <Upload size={14} /> {uploading ? "Laddar upp…" : "Ladda upp fil"}
                </button>
                {form.image_url && (
                  <button
                    type="button"
                    data-testid="modal-image-remove"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="ml-2 inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-white hover:border-red-400 hover:text-red-600 text-sm font-semibold px-4 py-2"
                  >
                    <ImageOff size={14} /> Ta bort bild
                  </button>
                )}
                <input
                  type="text"
                  placeholder="…eller klistra in bild-URL (https://…)"
                  data-testid="modal-image-url"
                  value={form.image_url || ""}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full bg-white border border-[var(--border)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Kategori</span>
            <select
              data-testid="modal-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Namn</span>
            <input
              required
              data-testid="modal-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Beskrivning</span>
            <textarea
              data-testid="modal-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Pris (kr)</span>
              <input
                type="number"
                step="1"
                required
                data-testid="modal-price"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Sortering</span>
              <input
                type="number"
                data-testid="modal-sort"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 bg-[var(--surface-alt)] border-t border-[var(--border)]">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold border border-[var(--border)] bg-white hover:bg-stone-50">
            Avbryt
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="modal-save"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Sparar…" : "Spara"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Alla");
  const [modal, setModal] = useState(null); // null | "create" | item object
  const role = user && user !== false ? user.role : null;
  const isAnalyst = role === "analyst";
  const isMenuEditor = role === "menu_editor";
  const isAdmin = role === "admin";
  const isDeveloper = role === "developer";
  const canManageUsers = isAdmin || isDeveloper;
  // Restricted roles (analyst / menu_editor) can only see specific tabs.
  const isRestricted = isAnalyst || isMenuEditor;
  const defaultTab = isAnalyst ? "stats" : "menu"; // menu_editor also lands on menu
  const [tab, setTab] = useState(defaultTab);

  const load = async () => {
    if (isAnalyst) return;
    const { data } = await api.get("/menu");
    setItems(data);
  };

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line
  }, [user]);

  // Snap restricted roles back if they land on a tab they can't access.
  useEffect(() => {
    if (isAnalyst && tab !== "stats" && tab !== "users") setTab("stats");
    if (isMenuEditor && !["menu", "stats", "users"].includes(tab)) setTab("menu");
  }, [isAnalyst, isMenuEditor, tab]);

  if (loading) return <div className="min-h-screen grid place-items-center text-[var(--text-muted)]">Laddar…</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  const save = async (payload) => {
    if (modal === "create") {
      await api.post("/menu", payload);
      toast.success("Maträtt tillagd");
    } else {
      const { id, ...rest } = payload;
      await api.put(`/menu/${modal.id}`, rest);
      toast.success("Maträtt uppdaterad");
    }
    await load();
  };

  const remove = async (it) => {
    if (!window.confirm(`Ta bort "${it.name}"?`)) return;
    try {
      await api.delete(`/menu/${it.id}`);
      toast.success("Borttagen");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const filtered = filter === "Alla" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="min-h-screen bg-[var(--surface-alt)]">
      <Toaster position="top-right" />
      <header className="bg-white border-b border-[var(--border)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-block w-8 h-8 rounded-full bg-[var(--primary)] text-white grid place-items-center font-bold font-serif-display text-lg">M</span>
            <div>
              <div className="font-serif-display text-lg font-bold leading-tight">Mon Ami · Admin</div>
              <div className="text-xs text-[var(--text-muted)]">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              target="_blank"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-white px-4 py-2 text-sm font-semibold hover:border-[var(--text)]"
            >
              <ExternalLink size={14} /> Visa sida
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--border)] px-4 py-2 text-sm font-semibold hover:border-red-400 hover:text-red-600"
            >
              <LogOut size={14} /> Logga ut
            </button>
          </div>
        </div>
        {/* Top-level tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
          <div className="inline-flex bg-[var(--surface-alt)] border border-[var(--border)] rounded-full p-1 flex-wrap">
            {!isAnalyst && (
            <button
              onClick={() => setTab("menu")}
              data-testid="admin-tab-menu"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "menu" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <UtensilsCrossed size={14} /> Meny
            </button>
            )}
            {isAdmin && (
            <button
              onClick={() => setTab("lunch")}
              data-testid="admin-tab-lunch"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "lunch" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <Calendar size={14} /> Lunch
            </button>
            )}
            {isAdmin && (
            <button
              onClick={() => setTab("daily")}
              data-testid="admin-tab-daily"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "daily" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <Sparkles size={14} /> Dagens förslag
            </button>
            )}
            {isAdmin && (
            <button
              onClick={() => setTab("gallery")}
              data-testid="admin-tab-gallery"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "gallery" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <Images size={14} /> Galleri
            </button>
            )}
            {isAdmin && (
            <button
              onClick={() => setTab("events")}
              data-testid="admin-tab-events"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "events" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <Music size={14} /> Händelser
            </button>
            )}
            {isAdmin && (
            <button
              onClick={() => setTab("site")}
              data-testid="admin-tab-site"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "site" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <LayoutDashboard size={14} /> Webbplats
            </button>
            )}
            <button
              onClick={() => setTab("stats")}
              data-testid="admin-tab-stats"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "stats" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <BarChart3 size={14} /> Statistik
            </button>
            <button
              onClick={() => setTab("users")}
              data-testid="admin-tab-users"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "users" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <Users size={14} /> {(isRestricted && !canManageUsers) ? "Mitt konto" : "Användare"}
            </button>
            {isAdmin && (
            <button
              onClick={() => setTab("audit")}
              data-testid="admin-tab-audit"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === "audit" ? "bg-white shadow-sm" : "text-[var(--text-muted)]"
              }`}
            >
              <Activity size={14} /> Aktivitet
            </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {(() => {
          const panels = {
            site: <SiteEditor />,
            lunch: <LunchEditor />,
            gallery: <GalleryEditor />,
            events: <EventsEditor />,
            users: <UsersEditor />,
            stats: <Analytics />,
            audit: <AuditLog />,
            daily: <DailySpecialEditor />,
          };
          if (panels[tab]) return panels[tab];
          return (
        <>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-black">Menyhanterare</h1>
            <p className="text-[var(--text-muted)]">{items.length} maträtter totalt</p>
          </div>
          <button
            onClick={() => setModal("create")}
            data-testid="admin-add-item-button"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-5 py-3 font-semibold shadow-lg shadow-[var(--primary)]/20"
          >
            <Plus size={16} /> Ny maträtt
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {["Alla", ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                filter === c
                  ? "bg-[var(--text)] text-white"
                  : "bg-white border border-[var(--border)] hover:border-[var(--text)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-alt)] text-xs uppercase tracking-wider text-[var(--text-muted)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Namn</th>
                <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Kategori</th>
                <th className="text-right px-5 py-3 font-semibold">Pris</th>
                <th className="text-right px-5 py-3 font-semibold">Åtgärder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`admin-row-${it.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center">
                        {it.image_url ? (
                          <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon size={16} className="text-[var(--text-muted)]" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{it.name}</div>
                        {it.description && <div className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-1">{it.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell text-[var(--text-muted)]">{it.category}</td>
                  <td className="px-5 py-4 text-right font-serif-display font-bold tabular-nums">{Math.round(it.price)} kr</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => setModal(it)}
                        data-testid={`admin-edit-${it.id}`}
                        className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--surface-alt)]"
                        title="Redigera"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => remove(it)}
                        data-testid={`admin-delete-${it.id}`}
                        className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 hover:text-red-600"
                        title="Ta bort"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-[var(--text-muted)]">
                    Inga maträtter i den här kategorin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
          );
        })()}
      </main>

      {modal && (
        <ItemModal
          initial={modal === "create" ? null : modal}
          onClose={() => setModal(null)}
          onSave={save}
        />
      )}
    </div>
  );
}
